import React, {useEffect, useState} from 'react';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

/* Common */
import { Button, Image, Icon } from '~/components/common';
import ProductSticker from '~/components/product-sticker/components/ProductSticker';
import PriceFormat from "../PriceFormat";
import FavoritesToggle from "~/components/favorites-toggle/components/FavoritesToggle";
import FavoritesRemoveButton from "~/components/favorites-toggle/components/FavoritesRemoveButton";

/* Config */
import { SWIPER_CONFIG } from '~/config/swiper';

/* Symbols */
import MarkerIcon from '~/../symbols/marker.svg';
import ChevronIcon from '~/../symbols/chevron.svg';

/* Utilities */
import { getCloudimageUrl, trans } from '~/utilities';

// Shown when the browser still has a translation dump from before these keys existed; the dump is cached per day.
const AVAILABILITY_FALLBACK_LABELS = {
    available: 'Direct beschikbaar',
    expected: 'Verwacht',
};

export default function CardProduct({
    image,
    images,
    location,
    title,
    subtitle,
    model,
    specs,
    href,
    price,
    lease,
    privateLease,
    businessLeaseVop,
    onlyPrivateLeasePrices = false,
    onlyOperationalLeasePrices = false,
    operationalLease,
    vehicleType,
    btwMarge,
    sticker,
    stickerTooltips,
    favorites,
    id,
    favoritesOverview,
    onFavoriteRemoved,
    userLoggedIn,
    extranetLoginUrl,
    extranetRegisterUrl,
    saveFavoriteUrl,
    removeFavoriteUrl,
    getFavoritesUrl,
    fullDesktopView = false,
    source,
    businessLeasePrice,
    hideLocation = false,
    hideFavorites = false,
    availability = null
}) {
    const [currentSlideIndex, setCurrentSlideIndex] = useState(1);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Helper function to check if model is Caddy Combi (case insensitive, ignoring spaces and hyphens)
    const isCaddyCombi = () => model && model.toLowerCase().replace(/[\s-]+/g, '').includes('caddycombi');

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <a href={href} className={`card card--product card--link text--sm text-decoration-none${fullDesktopView ? ` card--product--desktop-view` : ``}`}
            title={`${ title } ${ subtitle }`}
        >
            {(images?.length > 0 || image?.src) &&
                <div className="card__header">

                    {isMobile && !favoritesOverview && !hideFavorites && (
                        <FavoritesToggle
                            favorites={favorites}
                            vehicleId={id}
                            userLoggedIn={userLoggedIn}
                            extranetLoginUrl={extranetLoginUrl}
                            extranetRegisterUrl={extranetRegisterUrl}
                            saveFavoriteUrl={saveFavoriteUrl}
                            removeFavoriteUrl={removeFavoriteUrl}
                            getFavoritesUrl={getFavoritesUrl}
                        />
                    )}

                    {favoritesOverview ? (
                        <FavoritesRemoveButton
                            vehicleId={id}
                            onFavoriteRemoved={onFavoriteRemoved}
                            removeFavoriteUrl={removeFavoriteUrl}
                        />
                    ) : null}

                    {images?.length > 0 ? (
                        <Swiper
                            {...SWIPER_CONFIG}
                            modules={[Navigation, Pagination]}
                            navigation={{
                                prevEl: '.swiper-button-prev',
                                nextEl: '.swiper-button-next',
                            }}
                            pagination={{ clickable: true, }}
                            spaceBetween={0}
                            slidesPerView={1}
                            onSlideChange={(slide) => {
                                if (currentSlideIndex !== slide.activeIndex) {
                                    setCurrentSlideIndex(slide.activeIndex);
                                }
                            }}
                            loop
                        >
                            {images?.slice(0, 5).map((image, index) => (
                                <SwiperSlide key={index}>
                                    <Image
                                        className="card__image"
                                        src={getCloudimageUrl(image.src, { w: 536, h: 357, func: 'crop' })}
                                        srcSet={`${getCloudimageUrl(image.src, { w: 1072, h: 714, func: 'crop' })} 2x`}
                                        alt={image.alt}
                                        width={536}
                                        height={357}
                                        sources={[
                                            {
                                                set: {
                                                    '1x': getCloudimageUrl(image.src, { w: 368, h: 245, func: 'crop' }),
                                                    '2x': getCloudimageUrl(image.src, { w: 736, h: 490, func: 'crop' }),
                                                },
                                                media: '(min-width: 1660px)',
                                            },
                                            {
                                                set: {
                                                    '1x': getCloudimageUrl(image.src, { w: 425, h: 283, func: 'crop' }),
                                                    '2x': getCloudimageUrl(image.src, { w: 850, h: 566, func: 'crop' }),
                                                },
                                                media: '(min-width: 1440px)',
                                            },
                                            {
                                                set: {
                                                    '1x': getCloudimageUrl(image.src, { w: 372, h: 248, func: 'crop' }),
                                                    '2x': getCloudimageUrl(image.src, { w: 744, h: 496, func: 'crop' }),
                                                },
                                                media: '(min-width: 1280px)',
                                            },
                                            {
                                                set: {
                                                    '1x': getCloudimageUrl(image.src, { w: 438, h: 292, func: 'crop' }),
                                                    '2x': getCloudimageUrl(image.src, { w: 876, h: 584, func: 'crop' }),
                                                },
                                                media: '(min-width: 992px)',
                                            },
                                            {
                                                set: {
                                                    '1x': getCloudimageUrl(image.src, { w: 320, h: 213, func: 'crop' }),
                                                    '2x': getCloudimageUrl(image.src, { w: 640, h: 426, func: 'crop' }),
                                                },
                                                media: '(min-width: 768px)',
                                            },
                                            {
                                                set: {
                                                    '1x': getCloudimageUrl(image.src, { w: 356, h: 237, func: 'crop' }),
                                                    '2x': getCloudimageUrl(image.src, { w: 712, h: 474, func: 'crop' }),
                                                },
                                                media: '(min-width: 600px)',
                                            },
                                        ]}
                                    />
                                </SwiperSlide>
                            ))}
                            <div className="swiper-button-prev">
                                <Icon size={24} url={ChevronIcon} />
                            </div>
                            <div className="swiper-button-next">
                                <Icon size={24} url={ChevronIcon} />
                            </div>
                        </Swiper>
                    ) : (
                        <Image
                            className="card__image"
                            src={getCloudimageUrl(image.src, { w: 536, h: 357, func: 'crop' })}
                            srcSet={`${getCloudimageUrl(image.src, { w: 1072, h: 714, func: 'crop' })} 2x`}
                            alt={image.alt}
                            width={536}
                            height={357}
                            sources={[
                                {
                                    set: {
                                        '1x': getCloudimageUrl(image.src, { w: 425, h: 283, func: 'crop' }),
                                        '2x': getCloudimageUrl(image.src, { w: 850, h: 566, func: 'crop' }),
                                    },
                                    media: '(min-width: 1660px)',
                                },
                                {
                                    set: {
                                        '1x': getCloudimageUrl(image.src, { w: 313, h: 209, func: 'crop' }),
                                        '2x': getCloudimageUrl(image.src, { w: 626, h: 418, func: 'crop' }),
                                    },
                                    media: '(min-width: 1440px)',
                                },
                                {
                                    set: {
                                        '1x': getCloudimageUrl(image.src, { w: 372, h: 248, func: 'crop' }),
                                        '2x': getCloudimageUrl(image.src, { w: 744, h: 496, func: 'crop' }),
                                    },
                                    media: '(min-width: 1280px)',
                                },
                                {
                                    set: {
                                        '1x': getCloudimageUrl(image.src, { w: 285, h: 190, func: 'crop' }),
                                        '2x': getCloudimageUrl(image.src, { w: 570, h: 380, func: 'crop' }),
                                    },
                                    media: '(min-width: 992px)',
                                },
                                {
                                    set: {
                                        '1x': getCloudimageUrl(image.src, { w: 320, h: 213, func: 'crop' }),
                                        '2x': getCloudimageUrl(image.src, { w: 640, h: 426, func: 'crop' }),
                                    },
                                    media: '(min-width: 768px)',
                                },
                                {
                                    set: {
                                        '1x': getCloudimageUrl(image.src, { w: 356, h: 237, func: 'crop' }),
                                        '2x': getCloudimageUrl(image.src, { w: 712, h: 474, func: 'crop' }),
                                    },
                                    media: '(min-width: 600px)',
                                },
                            ]}
                        />
                    )}
                </div>
            }

            <div className="card-body d-flex flex-column">
                <ProductSticker
                    sticker={sticker}
                    stickerTooltips={stickerTooltips}
                />
                {/* ALMN shows neither: a lease company has no branch to visit and no login for favorites (WAAL-1244). */}
                {!(hideLocation && hideFavorites) && (
                <div className="d-flex flex-row justify-content-between align-items-center mb-1 gap-1">
                    {location && !hideLocation ? (
                        <span className={`${fullDesktopView ? 'text--14' : 'text--12 text--md-14'} mb-0`}>
                            <Icon size={16} url={MarkerIcon} className="mr-1" />
                            {location}
                        </span>
                    ) : null}

                    {!isMobile && !favoritesOverview && !hideFavorites && (
                        <FavoritesToggle
                            favorites={favorites}
                            vehicleId={id}
                            userLoggedIn={userLoggedIn}
                            extranetLoginUrl={extranetLoginUrl}
                            extranetRegisterUrl={extranetRegisterUrl}
                            saveFavoriteUrl={saveFavoriteUrl}
                            removeFavoriteUrl={removeFavoriteUrl}
                            getFavoritesUrl={getFavoritesUrl}
                        />
                    )}
                </div>
                )}

                {['available', 'expected'].includes(availability) ? (
                    <p className={`card__availability card__availability--${availability} ${fullDesktopView ? 'text--14' : 'text--12 text--md-14'} mb-1`}>
                        <span className="card__availability-dot" />
                        {trans(`app.vehicle_overview.vehicle_card.availability.${availability}`, {}, AVAILABILITY_FALLBACK_LABELS[availability])}
                    </p>
                ) : null}

                <p className="title h6 mb-0">
                    {title}
                </p>
                <p className={`${fullDesktopView ? 'text--14' : 'text--12 text--md-14'} mb-0 text-truncate`}>
                    {subtitle}
                </p>
                <div className="d-flex align-items-center">
                    {specs.map((spec, index) => (
                        <div key={index} className="d-flex align-items-center">
                            {index !== 0 ? (
                                <div className="dot"></div>
                            ) : null}
                            <p className={`${fullDesktopView ? 'text--14' : 'text--12 text--md-14'}`}>
                                {spec}
                            </p>
                        </div>
                    ))}
                </div>
                <hr className={`${fullDesktopView ? `d-block` : `d-none d-md-block`} my-3 w-100`}></hr>

                <>
                    <div className={`d-flex flex-column flex-md-row align-items-md-center gap-md-1 ${ vehicleType === 'BEDRIJF' && btwMarge === 'B' && !isCaddyCombi() || price?.discount > 0 ? '' : 'mb-md-4' }`}>
                        {onlyPrivateLeasePrices ? (
                            privateLease?.value ? (
                                <div className="d-flex flex-row gap-1">
                                    <p className="mb-0">Private lease vanaf</p>
                                    <span className="text--14 text--md-16 font-weight-semi-bold"><PriceFormat value={privateLease?.value} suffix=" p.m."/></span>
                                </div>
                            ) : (
                                <div className="d-flex flex-row gap-1">
                                    <p className="mb-0">Private lease - op aanvraag</p>
                                </div>
                            )
                        ) : onlyOperationalLeasePrices ? (
                            operationalLease?.value ? (
                                <div className="d-flex flex-row gap-1">
                                    <p className="mb-0">Operational lease vanaf</p>
                                    <span className="text--14 text--md-16 font-weight-semi-bold"><PriceFormat value={operationalLease?.value} suffix=" p.m."/></span>
                                </div>
                            ) : (
                                <div className="d-flex flex-row gap-1">
                                    <p className="mb-0">Operational lease - op aanvraag</p>
                                </div>
                            )
                        ) : source === 'company_cars' && businessLeasePrice ? (
                            <div className="d-flex flex-column gap-1">
                                <span className="text--14 text--md-16 font-weight-semi-bold"><PriceFormat value={businessLeasePrice} suffix=" p.m."/></span>
                                <p className="text--12 text--md-14 mb-0">Operational lease, 48 mnd / 10.000 km p.j.</p>
                            </div>
                        ) : (
                            <>
                                <span className="text--14 text--md-16 font-weight-semi-bold"><PriceFormat value={price?.discount > 0 ? price?.discount : price?.value} /></span>
                                {lease?.value ? (
                                    <div className="d-flex flex-row gap-1">
                                    <p className="mb-0">of vanaf</p>
                                    <span className="text--14 text--md-16 font-weight-semi-bold"><PriceFormat value={lease?.value} suffix=" p.m."/></span>
                                    </div>
                                ) : null}
                            </>
                        )}
                    </div>
                    {price?.discount > 0 && !onlyPrivateLeasePrices && !onlyOperationalLeasePrices ? (
                        <div className={`d-flex flex-row gap-1 ${vehicleType === 'BEDRIJF' && btwMarge === 'B' && !isCaddyCombi() ? '' : 'mb-md-4'}`}>
                            <del className="text-muted--60 text--14"><PriceFormat value={price?.value} /></del>
                            <p className="text-danger text--14 font-weight-medium mb-0">
                                {trans(`app.vehicle_overview.vehicle_card.purchase.discount`, { 'discount': (price?.value - price?.discount).toLocaleString('nl-NL') })}
                            </p>
                        </div>
                    ) : null}
                    {vehicleType === 'BEDRIJF' && btwMarge === 'B' && !isCaddyCombi() && (
                        <p className="text--12 text--md-14 mb-0 mb-md-4">
                            {trans(`app.vehicle_overview.vehicle_card.purchase.btw`)}
                        </p>
                    )}
                </>

                <div className={`${fullDesktopView ? `d-flex mt-2 mt-sm-auto` : `d-none d-md-flex mt-auto`} justify-content-between`}>
                    <Button
                        className="btn-sm"
                        theme="outline-base"
                        title={trans('app.vehicle_overview.detail_button')}
                        href={businessLeaseVop ? `${ href }?type=business` : href}
                        showIcon={false}
                    />
                </div>
            </div>
        </a>
    );
}
